/**
 * Insights Service - Generates smart financial insights from real data
 *
 * Features:
 * - Savings rate analysis
 * - Spending pattern detection
 * - Budget variance warnings
 * - Cash flow health indicators
 *
 * i18n (F5 Phase 2): every insight is a FULL-SENTENCE catalog template
 * (insights.* keys, {placeholders}) resolved through the i18n singleton at
 * generation time - never assembled from fragments, never a hardcoded '$'
 * (amounts go through the central currency formatter). The caller
 * (AnalyticsPage insights memo) re-generates on language change.
 *
 * Performance: All calculations are O(n) where n = number of transactions
 */

import { t, categoryDisplayName } from '@/i18n';
import { formatCurrency } from '@/utils/format';

// ============================================================
// Types
// ============================================================

export interface Insight {
  type: 'warning' | 'tip' | 'success' | 'info';
  title: string;
  description: string;
  priority: number; // 1 = highest
}

export interface InsightInput {
  totalIncome: number;
  totalExpense: number;
  categorySpending: Array<{
    categoryName: string;
    amount: number;
    percentage: number;
  }>;
  monthlyTrends: Array<{
    month: string;
    monthKey?: string;
    income: number;
    expense: number;
    net: number;
  }>;
  currentBalance: number;
  detectedBillsTotal: number;
  detectedIncomeTotal: number;
  // Smart-insight extras (all optional - insights degrade gracefully)
  expenseCount?: number;
  largestExpense?: { amount: number; description: string } | null;
  weekendExpense?: number;
  spendDays?: number;
  activeDays?: number;
  uncategorized?: { amount: number; count: number } | null;
  /** Most active watchlist group this period (from watchlist service) */
  watchlistTop?: {
    name: string;
    expense: number;
    count: number;
    changePct: number | null;
  } | null;
}

// Amounts in insight prose match the old "$1,234" look (no decimals).
function money(amount: number): string {
  return formatCurrency(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// ============================================================
// Insight Generators
// ============================================================

/**
 * Generate savings rate insight.
 */
function generateSavingsInsight(income: number, expense: number): Insight | null {
  if (income <= 0) return null;

  const savingsRate = ((income - expense) / income) * 100;
  const saved = income - expense;

  if (savingsRate >= 20) {
    return {
      type: 'success',
      title: t('insights.savings_excellent.title'),
      description: t('insights.savings_excellent.desc', {
        rate: savingsRate.toFixed(0),
        amount: money(saved),
      }),
      priority: 3,
    };
  }

  if (savingsRate >= 10) {
    return {
      type: 'success',
      title: t('insights.savings_good.title'),
      description: t('insights.savings_good.desc', {
        rate: savingsRate.toFixed(0),
        amount: money(saved),
      }),
      priority: 4,
    };
  }

  if (savingsRate >= 0) {
    return {
      type: 'info',
      title: t('insights.savings_rate.title'),
      description: t('insights.savings_rate.desc', { rate: savingsRate.toFixed(0) }),
      priority: 5,
    };
  }

  // Negative savings
  return {
    type: 'warning',
    title: t('insights.overspending.title'),
    description: t('insights.overspending.desc', { amount: money(Math.abs(saved)) }),
    priority: 1,
  };
}

/**
 * Generate top spending category insight.
 */
function generateSpendingInsight(
  categorySpending: InsightInput['categorySpending']
): Insight | null {
  if (categorySpending.length === 0) return null;

  const top = categorySpending[0];
  const topName = categoryDisplayName(top.categoryName);

  if (top.percentage >= 50) {
    return {
      type: 'warning',
      title: t('insights.spending_concentrated.title'),
      description: t('insights.spending_concentrated.desc', {
        category: topName,
        percent: top.percentage.toFixed(0),
        amount: money(top.amount),
      }),
      priority: 2,
    };
  }

  if (top.percentage >= 30) {
    return {
      type: 'info',
      title: t('insights.spending_top.title'),
      description: t('insights.spending_top.desc', {
        category: topName,
        percent: top.percentage.toFixed(0),
        amount: money(top.amount),
      }),
      priority: 6,
    };
  }

  return {
    type: 'success',
    title: t('insights.spending_balanced.title'),
    description: t('insights.spending_balanced.desc', {
      category: topName,
      percent: top.percentage.toFixed(0),
    }),
    priority: 7,
  };
}

/**
 * Generate month-over-month trend insight.
 */
function generateTrendInsight(
  monthlyTrends: InsightInput['monthlyTrends']
): Insight | null {
  if (monthlyTrends.length < 2) return null;

  const current = monthlyTrends[monthlyTrends.length - 1];
  const previous = monthlyTrends[monthlyTrends.length - 2];

  if (!current || !previous) return null;

  const expenseChange = previous.expense > 0
    ? ((current.expense - previous.expense) / previous.expense) * 100
    : 0;

  const incomeChange = previous.income > 0
    ? ((current.income - previous.income) / previous.income) * 100
    : 0;

  // Expense increased significantly
  if (expenseChange > 20) {
    return {
      type: 'warning',
      title: t('insights.trend_spending_up.title'),
      description: t('insights.trend_spending_up.desc', {
        percent: expenseChange.toFixed(0),
        amount: money(current.expense - previous.expense),
      }),
      priority: 2,
    };
  }

  // Expense decreased significantly
  if (expenseChange < -15) {
    return {
      type: 'success',
      title: t('insights.trend_spending_down.title'),
      description: t('insights.trend_spending_down.desc', {
        percent: Math.abs(expenseChange).toFixed(0),
      }),
      priority: 3,
    };
  }

  // Income increased significantly
  if (incomeChange > 20) {
    return {
      type: 'success',
      title: t('insights.trend_income_up.title'),
      description: t('insights.trend_income_up.desc', {
        percent: incomeChange.toFixed(0),
        amount: money(current.income - previous.income),
      }),
      priority: 4,
    };
  }

  return null;
}

/**
 * Generate cash flow health insight.
 */
function generateCashFlowInsight(
  balance: number,
  monthlyBills: number,
  _monthlyIncome: number
): Insight | null {
  if (monthlyBills <= 0) return null;

  const monthsCovered = balance / monthlyBills;

  if (monthsCovered < 1) {
    return {
      type: 'warning',
      title: t('insights.cash_low.title'),
      description: t('insights.cash_low.desc', { months: monthsCovered.toFixed(1) }),
      priority: 1,
    };
  }

  if (monthsCovered >= 6) {
    return {
      type: 'success',
      title: t('insights.cash_healthy.title'),
      description: t('insights.cash_healthy.desc', { months: monthsCovered.toFixed(0) }),
      priority: 5,
    };
  }

  if (monthsCovered >= 3) {
    return {
      type: 'info',
      title: t('insights.cash_adequate.title'),
      description: t('insights.cash_adequate.desc', { months: monthsCovered.toFixed(1) }),
      priority: 6,
    };
  }

  return {
    type: 'tip',
    title: t('insights.cash_build.title'),
    description: t('insights.cash_build.desc', { months: monthsCovered.toFixed(1) }),
    priority: 4,
  };
}

/**
 * Generate recurring bills tip.
 */
function generateBillsTip(
  monthlyBills: number,
  monthlyIncome: number
): Insight | null {
  if (monthlyIncome <= 0 || monthlyBills <= 0) return null;

  const billsPercent = (monthlyBills / monthlyIncome) * 100;

  if (billsPercent > 70) {
    return {
      type: 'warning',
      title: t('insights.bills_high.title'),
      description: t('insights.bills_high.desc', { percent: billsPercent.toFixed(0) }),
      priority: 2,
    };
  }

  if (billsPercent > 50) {
    return {
      type: 'tip',
      title: t('insights.bills_review.title'),
      description: t('insights.bills_review.desc', { percent: billsPercent.toFixed(0) }),
      priority: 5,
    };
  }

  return null;
}

/**
 * Month-pace projection: extrapolates the current partial month and compares
 * against last month's actual spending. Only fires with >=7 days of signal
 * and a real previous-month baseline, and only when the difference is
 * meaningful (>=15% either way) - silence beats noise.
 */
function generatePaceInsight(
  monthlyTrends: InsightInput['monthlyTrends']
): Insight | null {
  if (monthlyTrends.length < 2) return null;

  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const current = monthlyTrends[monthlyTrends.length - 1];
  const previous = monthlyTrends[monthlyTrends.length - 2];

  // Only project when the last trend bucket really is the current month
  if (!current.monthKey || current.monthKey !== currentKey) return null;

  const dayOfMonth = now.getDate();
  if (dayOfMonth < 7) return null; // Too early to extrapolate
  if (previous.expense <= 0 || current.expense <= 0) return null;

  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const projected = (current.expense / dayOfMonth) * daysInMonth;
  const changePct = ((projected - previous.expense) / previous.expense) * 100;

  if (changePct >= 15) {
    return {
      type: 'warning',
      title: t('insights.pace_up.title'),
      description: t('insights.pace_up.desc', {
        amount: money(projected),
        percent: changePct.toFixed(0),
      }),
      priority: 2,
    };
  }

  if (changePct <= -15) {
    return {
      type: 'success',
      title: t('insights.pace_down.title'),
      description: t('insights.pace_down.desc', {
        amount: money(projected),
        percent: Math.abs(changePct).toFixed(0),
      }),
      priority: 4,
    };
  }

  return null;
}

/**
 * Largest single expense - surfaces the one transaction that dominated the
 * period (>=15% of all spending).
 */
function generateLargestExpenseInsight(
  largestExpense: InsightInput['largestExpense'],
  totalExpense: number
): Insight | null {
  if (!largestExpense || totalExpense <= 0) return null;

  const share = (largestExpense.amount / totalExpense) * 100;
  if (share < 15) return null;

  return {
    type: 'info',
    title: t('insights.largest_expense.title'),
    description: t('insights.largest_expense.desc', {
      amount: money(largestExpense.amount),
      description: largestExpense.description || t('insights.largest_expense.no_desc'),
      percent: share.toFixed(0),
    }),
    priority: 6,
  };
}

/**
 * Weekend-heavy spending: weekends are ~29% of days, so >=40% of spending on
 * Sat/Sun is a real pattern worth pointing out.
 */
function generateWeekendInsight(
  weekendExpense: number | undefined,
  totalExpense: number,
  expenseCount: number | undefined
): Insight | null {
  if (!weekendExpense || totalExpense <= 0) return null;
  if ((expenseCount ?? 0) < 10) return null; // Not enough signal

  const share = (weekendExpense / totalExpense) * 100;
  if (share < 40) return null;

  return {
    type: 'tip',
    title: t('insights.weekend_heavy.title'),
    description: t('insights.weekend_heavy.desc', {
      percent: share.toFixed(0),
      amount: money(weekendExpense),
    }),
    priority: 6,
  };
}

/**
 * No-spend days - positive reinforcement only (fires when >=25% of elapsed
 * days had zero spending, with at least two weeks of data).
 */
function generateNoSpendInsight(
  spendDays: number | undefined,
  activeDays: number | undefined
): Insight | null {
  if (spendDays === undefined || !activeDays || activeDays < 14) return null;

  const noSpendDays = activeDays - spendDays;
  if (noSpendDays <= 0 || noSpendDays < activeDays * 0.25) return null;

  return {
    type: 'success',
    title: t('insights.no_spend_days.title'),
    description: t('insights.no_spend_days.desc', { days: noSpendDays }),
    priority: 7,
  };
}

/**
 * Uncategorized nudge - only when it materially blurs the picture
 * (>=5 transactions AND >=10% of spending).
 */
function generateUncategorizedInsight(
  uncategorized: InsightInput['uncategorized'],
  totalExpense: number
): Insight | null {
  if (!uncategorized || uncategorized.count < 5 || totalExpense <= 0) return null;
  if (uncategorized.amount < totalExpense * 0.1) return null;

  return {
    type: 'tip',
    title: t('insights.uncategorized.title'),
    description: t('insights.uncategorized.desc', {
      count: uncategorized.count,
      amount: money(uncategorized.amount),
    }),
    priority: 5,
  };
}

/**
 * Watchlist mover - connects the keyword tracker to insights ("AI Tools hit
 * $62 this period, up 45%"). Warning when clearly accelerating, info when
 * simply active.
 */
function generateWatchlistInsight(
  watchlistTop: InsightInput['watchlistTop']
): Insight | null {
  if (!watchlistTop || watchlistTop.expense <= 0) return null;

  if (watchlistTop.changePct !== null && watchlistTop.changePct >= 30) {
    return {
      type: 'warning',
      title: t('insights.watch_up.title', { name: watchlistTop.name }),
      description: t('insights.watch_up.desc', {
        name: watchlistTop.name,
        amount: money(watchlistTop.expense),
        percent: watchlistTop.changePct.toFixed(0),
      }),
      priority: 3,
    };
  }

  if (watchlistTop.count < 2) return null; // One stray match is not a pattern

  return {
    type: 'info',
    title: t('insights.watch_activity.title', { name: watchlistTop.name }),
    description: t('insights.watch_activity.desc', {
      name: watchlistTop.name,
      amount: money(watchlistTop.expense),
      count: watchlistTop.count,
    }),
    priority: 7,
  };
}

// ============================================================
// Main Function
// ============================================================

/**
 * Generate insights from financial data.
 * Returns top 6 most relevant insights sorted by priority.
 */
export function generateInsights(input: InsightInput): Insight[] {
  const insights: Insight[] = [];

  // Generate all possible insights
  const savings = generateSavingsInsight(input.totalIncome, input.totalExpense);
  if (savings) insights.push(savings);

  const spending = generateSpendingInsight(input.categorySpending);
  if (spending) insights.push(spending);

  const trend = generateTrendInsight(input.monthlyTrends);
  if (trend) insights.push(trend);

  const cashFlow = generateCashFlowInsight(
    input.currentBalance,
    input.detectedBillsTotal,
    input.detectedIncomeTotal
  );
  if (cashFlow) insights.push(cashFlow);

  const bills = generateBillsTip(input.detectedBillsTotal, input.detectedIncomeTotal);
  if (bills) insights.push(bills);

  // Smart insights v2 (2026-07) - each degrades to null without its input
  const pace = generatePaceInsight(input.monthlyTrends);
  if (pace) insights.push(pace);

  const largest = generateLargestExpenseInsight(input.largestExpense, input.totalExpense);
  if (largest) insights.push(largest);

  const weekend = generateWeekendInsight(
    input.weekendExpense,
    input.totalExpense,
    input.expenseCount
  );
  if (weekend) insights.push(weekend);

  const noSpend = generateNoSpendInsight(input.spendDays, input.activeDays);
  if (noSpend) insights.push(noSpend);

  const uncategorized = generateUncategorizedInsight(input.uncategorized, input.totalExpense);
  if (uncategorized) insights.push(uncategorized);

  const watch = generateWatchlistInsight(input.watchlistTop);
  if (watch) insights.push(watch);

  // Sort by priority (1 = highest) and return top 6
  return insights
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 6);
}

export default generateInsights;
